/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as PIXI from 'pixi.js';
import { Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { XY } from '../../utils/MathUtils';
import { FXParticleEmitter } from './FXParticleEmitter';
import type { FXParticleEmitterConfig } from './FXParticleEmitterConfig';

const particleTicker = new Ticker();
particleTicker.start();

export class FXParticleLayer extends PIXI.Container {
  public readonly onRemovedAllActiveEmitters = new Signal();

  private readonly activeEmitters: FXParticleEmitter[];

  private readonly inactiveEmitters: FXParticleEmitter[];

  private readonly secondaryContainers: PIXI.Container[] = [];

  private readonly primaryContainer: PIXI.Container;

  public updateEnabled: boolean;

  private readonly scrolled: XY;

  constructor(autoUpdate = false) {
    super();

    this.updateEnabled = true;

    this.activeEmitters = [];

    this.inactiveEmitters = [];

    this.primaryContainer = new PIXI.Container();
    this.addChild(this.primaryContainer);

    this.secondaryContainers = [];

    this.scrolled = { x: 0, y: 0 };

    if (autoUpdate) this.setAutoUpdate();
  }

  private setAutoUpdate(): void {
    const callback = (): void => {
      this.update(particleTicker.deltaTime);
    };
    particleTicker.add(callback);
    this.once('destroyed', () => {
      particleTicker.remove(callback);
    });
  }

  public getSecondaryContainer(): PIXI.Container {
    if (this.secondaryContainers.length > 0) {
      const batch = this.secondaryContainers.pop()!;
      if (batch.destroyed) {
        return new PIXI.Container();
      }
      batch.rotation = 0;
      batch.scale.set(1);
      batch.position.set(0, 0);
      return batch;
    }
    return new PIXI.Container();
  }

  public addParticleEmitter(x: number, y: number, emitterConfig: FXParticleEmitterConfig): FXParticleEmitter {
    const { forceOwnLayer } = emitterConfig;
    const container = forceOwnLayer ? this.getSecondaryContainer() : this.primaryContainer;
    let emitter;
    if (this.inactiveEmitters.length > 0) {
      emitter = this.inactiveEmitters.pop()!;
      emitter.container = container;
      emitter.resetWithConfig(emitterConfig);
    } else {
      emitter = new FXParticleEmitter(emitterConfig, container);
    }

    this.addChild(emitter.container);
    this.activeEmitters.push(emitter);
    emitter.x = x - this.scrolled.x;
    emitter.y = y - this.scrolled.y;
    return emitter;
  }

  public updateParticleEmitters(deltaTime: number): void {
    let emitter;
    for (let i = this.activeEmitters.length - 1; i >= 0; i -= 1) {
      emitter = this.activeEmitters[i];
      emitter.update(deltaTime);

      // remove inactive emitters, and store for reuse
      if (emitter.isCompleted) {
        // add secondary batches back to the pool
        if (emitter.container !== this.primaryContainer) {
          this.removeChild(emitter.container);
          this.secondaryContainers.push(emitter.container);
        }
        // splice and pool emitters if possible
        this.activeEmitters.splice(i, 1);
        if (this.activeEmitters.length === 0) {
          this.onRemovedAllActiveEmitters.emit();
        }
        this.inactiveEmitters.push(emitter);
      }
    }
  }

  public update(deltaTime: number, scrollVelocity?: XY): void {
    if (!this.updateEnabled || !this.visible) return;

    this.updateParticleEmitters(deltaTime);

    if (scrollVelocity) {
      const deltaX = scrollVelocity.x * deltaTime;
      const deltaY = scrollVelocity.y * deltaTime;
      this.scrolled.x += deltaX;
      this.scrolled.y += deltaY;
      this.x += deltaX;
      this.y += deltaY;
    }
  }

  /**
   * Fast-forwards all active emitters by the specified duration
   */
  public fastForward(duration: number, fps: number): void {
    let emitter: FXParticleEmitter;
    for (let i = this.activeEmitters.length - 1; i >= 0; i -= 1) {
      emitter = this.activeEmitters[i];
      emitter.fastForward(duration, fps);
    }
  }

  /**
   * Clear all emitters / configs from the display and repool
   */
  public clear(): void {
    for (let i = this.activeEmitters.length - 1; i >= 0; i -= 1) {
      // remove active emitter and clear its configs
      const emitter = this.activeEmitters.splice(i, 1)[0];
      emitter.clear();
      this.inactiveEmitters.push(emitter);
      // add secondary batches back to the pool
      if (emitter.container !== this.primaryContainer) {
        emitter.container.parent?.removeChild(emitter.container);
        this.secondaryContainers.push(emitter.container);
      }
    }
  }

  public destroy(): void {
    this.clear();
    this.inactiveEmitters.forEach((emitter) => {
      emitter.destroy();
    });
    super.destroy();
  }

  get particleCount(): number {
    return this.activeEmitters.reduce((a, e) => a + e.activeParticleCount, 0);
  }

  public hasActiveEmitters(): boolean {
    return this.activeEmitters.length > 0;
  }
}
